/**
 * Client identity (spec §2.1): `{ playerId, playerToken, name, avatar }` in localStorage.
 *
 * - `playerId` is a public uuid v4, `playerToken` a secret (32 random bytes as 64 hex chars).
 * - Created lazily on first access; name/avatar are editable later (avatar must be in `AVATARS`).
 * - Robust against corrupted or blocked storage: invalid parts are regenerated, storage failures
 *   fall back to an in-memory copy (see `lib/storage`).
 */
import type { ClientProfile } from '@20fight/server/protocol';
import { isAvatar, randomAvatar } from '@/lib/avatars';
import { cleanName, nameProblem } from '@/lib/profile';
import { PLAYER_TOKEN_RE, UUID_V4_RE, randomHex, uuidV4 } from '@/lib/random';
import { normalizeRoomCode, isValidRoomCode } from '@/lib/room-code';
import { readItem, readJson, removeItem, writeItem, writeJson } from '@/lib/storage';

export const IDENTITY_KEY = '20fight:identity';
export const LAST_ROOM_KEY = '20fight:lastRoom';

export type Identity = ClientProfile;

let cached: Identity | null = null;

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

/** Validate a stored identity; keep every valid part, regenerate the rest. */
function sanitize(raw: unknown): { identity: Identity; changed: boolean } {
  const obj = raw !== null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const id = str(obj.playerId);
  const token = str(obj.playerToken);
  const name = str(obj.name);
  const avatar = str(obj.avatar);

  // The id and token belong together: a fresh id needs a fresh token and vice versa.
  const credsOk =
    id !== null && UUID_V4_RE.test(id) && token !== null && PLAYER_TOKEN_RE.test(token);
  const identity: Identity = {
    playerId: credsOk ? id : uuidV4(),
    playerToken: credsOk ? token : randomHex(32),
    name: name !== null && nameProblem(name) === null ? cleanName(name) : '',
    avatar: isAvatar(avatar) ? avatar : randomAvatar(),
  };
  const changed = !credsOk || identity.name !== name || identity.avatar !== avatar;
  return { identity, changed };
}

/** The identity, created (and persisted) on first use. */
export function getIdentity(): Identity {
  if (cached) return cached;
  const { identity, changed } = sanitize(readJson(IDENTITY_KEY));
  if (changed) writeJson(IDENTITY_KEY, identity);
  cached = identity;
  return identity;
}

/** True once the user picked a valid username. */
export function hasProfile(identity: Identity = getIdentity()): boolean {
  return nameProblem(identity.name) === null;
}

/** Update name and/or avatar. The name is trimmed; an invalid name throws. */
export function updateProfile(patch: { name?: string; avatar?: string }): Identity {
  const current = getIdentity();
  const next: Identity = { ...current };
  if (patch.name !== undefined) {
    const problem = nameProblem(patch.name);
    if (problem) throw new Error(`invalid name: ${problem}`);
    next.name = cleanName(patch.name);
  }
  if (patch.avatar !== undefined && isAvatar(patch.avatar)) next.avatar = patch.avatar;
  cached = next;
  writeJson(IDENTITY_KEY, next);
  return next;
}

/** Code of the last joined room (for "Odaya geri dön"). */
export function getLastRoom(): string | null {
  const v = readItem(LAST_ROOM_KEY);
  return v && isValidRoomCode(v) ? normalizeRoomCode(v) : null;
}

export function setLastRoom(code: string): void {
  writeItem(LAST_ROOM_KEY, normalizeRoomCode(code));
}

export function clearLastRoom(): void {
  removeItem(LAST_ROOM_KEY);
}

/** Test helper: drop the cached identity so the next read goes to storage again. */
export function _resetIdentityCache(): void {
  cached = null;
}
